import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { 
  Package, 
  CreditCard, 
  Plus, 
  Check, 
  ChevronRight, 
  ChevronLeft,
  Star,
  AlertCircle,
  Loader2,
  CheckCircle2,
  ExternalLink,
  Mail,
  Phone
} from "lucide-react";
import { Helmet } from "react-helmet-async";
import { API_URL } from "../utils/api";
import { trackFunnel } from "../utils/trackFunnel";

// Progress indicator component
const ProgressIndicator = ({ currentStep, steps, content }) => (
  <div className="flex items-center justify-center mb-8">
    {steps.map((step, index) => {
      const stepNumber = index + 1;
      const isActive = stepNumber === currentStep;
      const isCompleted = stepNumber < currentStep;
      const stepLabel = content?.[`progress_step${stepNumber}`] || step;
      
      return (
        <div key={stepNumber} className="flex items-center">
          <div className={`flex items-center justify-center w-10 h-10 rounded-full text-sm font-medium transition-colors ${
            isActive 
              ? "bg-[#255E69] text-white" 
              : isCompleted 
                ? "bg-green-500 text-white" 
                : "bg-gray-200 text-gray-500"
          }`}>
            {isCompleted ? <Check className="w-5 h-5" /> : stepNumber}
          </div>
          <span className={`ml-2 text-sm font-medium hidden sm:inline ${
            isActive ? "text-[#255E69]" : "text-gray-500"
          }`}>
            {stepLabel}
          </span>
          {index < steps.length - 1 && (
            <div className={`w-8 sm:w-16 h-1 mx-2 rounded ${
              isCompleted ? "bg-green-500" : "bg-gray-200"
            }`} />
          )}
        </div>
      );
    })}
  </div>
);

// Selection card component
const SelectionCard = ({ item, isSelected, onSelect, type, content }) => {
  const isSubscription = type === "subscription";
  const isAddon = type === "addon";
  const isMonthly = isSubscription || (isAddon && item.price_type === "monthly");
  const hasCampaign = item.campaign_active && item.campaign_price;
  
  // Get campaign text
  const getCampaignText = () => {
    if (item.campaign_text) return item.campaign_text;
    if (isMonthly && item.campaign_duration) {
      return `${item.campaign_price.toLocaleString("sv-SE")} kr/mån i ${item.campaign_duration} månader, därefter ${item.price.toLocaleString("sv-SE")} kr/mån`;
    }
    if (isMonthly) {
      return content?.campaign_price_no_duration
        ?.replace("{campaign_price}", item.campaign_price.toLocaleString("sv-SE"))
        ?.replace("{regular_price}", item.price.toLocaleString("sv-SE"))
        || `${item.campaign_price.toLocaleString("sv-SE")} kr/mån (ordinarie ${item.price.toLocaleString("sv-SE")} kr/mån)`;
    }
    return content?.campaign_onetime_template
      ?.replace("{campaign_price}", item.campaign_price.toLocaleString("sv-SE"))
      ?.replace("{regular_price}", item.price.toLocaleString("sv-SE"))
      || `${item.campaign_price.toLocaleString("sv-SE")} kr (ord. ${item.price.toLocaleString("sv-SE")} kr)`;
  };
  
  return (
    <button
      onClick={() => onSelect(item)}
      className={`w-full text-left p-5 rounded-xl border-2 transition-all relative ${
        isSelected 
          ? "border-[#255E69] bg-[#255E69]/5 shadow-md" 
          : hasCampaign
            ? "border-orange-300 hover:border-orange-400 hover:shadow-sm bg-orange-50/50"
            : "border-gray-200 hover:border-gray-300 hover:shadow-sm"
      }`}
    >
      {/* Campaign ribbon */}
      {hasCampaign && (
        <div className="absolute -top-3 left-4 px-3 py-1 bg-orange-500 text-white text-xs font-semibold rounded-full shadow-sm">
          {content?.campaign_badge_text || "Kampanj"}
        </div>
      )}
      
      <div className="flex justify-between items-start mb-3 mt-1">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="text-lg font-semibold text-gray-900">{item.name}</h3>
          {item.is_recommended && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-full">
              <Star className="w-3 h-3" />
              {content?.recommended_text || "Rekommenderat"}
            </span>
          )}
          {item.badge_text && !item.is_recommended && !hasCampaign && (
            <span className="px-2 py-0.5 bg-[#255E69] text-white text-xs font-medium rounded-full">
              {item.badge_text}
            </span>
          )}
        </div>
        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
          isSelected ? "border-[#255E69] bg-[#255E69]" : "border-gray-300"
        }`}>
          {isSelected && <Check className="w-4 h-4 text-white" />}
        </div>
      </div>
      
      <p className="text-sm text-gray-600 mb-4">{item.description}</p>
      
      {/* Price display with campaign support */}
      <div className="mb-4">
        {hasCampaign ? (
          <div className="space-y-1">
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="text-2xl font-bold text-orange-600">
                {item.campaign_price.toLocaleString("sv-SE")}
              </span>
              <span className="text-orange-600">kr</span>
              {isMonthly && <span className="text-orange-500">{content?.per_month_text || "/mån"}</span>}
              <span className="text-sm text-gray-400 line-through">
                {item.price.toLocaleString("sv-SE")} kr
              </span>
            </div>
            {(isMonthly && item.campaign_duration) && (
              <p className="text-xs text-orange-600 font-medium">
                {getCampaignText()}
              </p>
            )}
          </div>
        ) : (
          <div className="flex items-baseline gap-1">
            <span className="text-2xl font-bold text-[#255E69]">
              {item.price.toLocaleString("sv-SE")}
            </span>
            <span className="text-gray-600">kr</span>
            {isMonthly && (
              <span className="text-gray-500">{content?.per_month_text || "/mån"}</span>
            )}
          </div>
        )}
      </div>
      
      {item.features && item.features.length > 0 && (
        <ul className="space-y-2">
          {item.features.map((feature, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
              <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
              {feature}
            </li>
          ))}
        </ul>
      )}
      
      {isAddon && item.category && (
        <p className="text-xs text-gray-400 mt-3">Kategori: {item.category}</p>
      )}
    </button>
  );
};

// Addon card with checkbox style
const AddonCard = ({ item, isSelected, onToggle, content }) => {
  const isMonthly = item.price_type === "monthly";
  const hasCampaign = item.campaign_active && item.campaign_price;
  
  return (
    <button
      onClick={() => onToggle(item)}
      className={`w-full text-left p-4 rounded-lg border-2 transition-all relative ${
        isSelected 
          ? "border-[#255E69] bg-[#255E69]/5" 
          : hasCampaign
            ? "border-orange-300 hover:border-orange-400 bg-orange-50/50"
            : "border-gray-200 hover:border-gray-300"
      }`}
    >
      {/* Campaign badge */}
      {hasCampaign && (
        <div className="absolute -top-2 left-3 px-2 py-0.5 bg-orange-500 text-white text-xs font-semibold rounded-full">
          {content?.campaign_badge_text || "Kampanj"}
        </div>
      )}
      
      <div className="flex justify-between items-start mt-1">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="font-medium text-gray-900">{item.name}</h4>
            {item.badge_text && !hasCampaign && (
              <span className="px-2 py-0.5 bg-[#255E69] text-white text-xs rounded-full">
                {item.badge_text}
              </span>
            )}
          </div>
          <p className="text-sm text-gray-600 mb-2">{item.description}</p>
          
          {/* Price with campaign support */}
          {hasCampaign ? (
            <div className="space-y-0.5">
              <div className="flex items-baseline gap-2">
                <span className="font-semibold text-orange-600">
                  {item.campaign_price.toLocaleString("sv-SE")} kr
                </span>
                {isMonthly && (
                  <span className="text-sm text-orange-500">{content?.per_month_text || "/mån"}</span>
                )}
                <span className="text-sm text-gray-400 line-through">
                  {item.price.toLocaleString("sv-SE")} kr
                </span>
              </div>
              {isMonthly && item.campaign_duration && (
                <p className="text-xs text-orange-600">
                  {item.campaign_text || `i ${item.campaign_duration} mån, därefter ${item.price.toLocaleString("sv-SE")} kr/mån`}
                </p>
              )}
            </div>
          ) : (
            <div className="flex items-baseline gap-1">
              <span className="font-semibold text-[#255E69]">
                {item.price.toLocaleString("sv-SE")} kr
              </span>
              {isMonthly && (
                <span className="text-sm text-gray-500">{content?.per_month_text || "/mån"}</span>
              )}
            </div>
          )}
        </div>
        <div className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-colors ${
          isSelected ? "border-[#255E69] bg-[#255E69]" : "border-gray-300"
        }`}>
          {isSelected && <Check className="w-4 h-4 text-white" />}
        </div>
      </div>
    </button>
  );
};

// Summary component
const Summary = ({ selectedPackage, selectedSubscription, selectedAddons, content }) => {
  // Helper function to get effective price
  const getEffectivePrice = (item, isMonthly = false) => {
    if (item?.campaign_active && item?.campaign_price != null) {
      return item.campaign_price;
    }
    return item?.price || 0;
  };
  
  const onetimeTotal = getEffectivePrice(selectedPackage, false) + 
    selectedAddons.filter(a => a.price_type !== "monthly").reduce((sum, a) => sum + getEffectivePrice(a, false), 0);
  
  const monthlyTotal = getEffectivePrice(selectedSubscription, true) + 
    selectedAddons.filter(a => a.price_type === "monthly").reduce((sum, a) => sum + getEffectivePrice(a, true), 0);
  
  // Check if any item has an active campaign
  const packageHasCampaign = selectedPackage?.campaign_active && selectedPackage?.campaign_price;
  const subscriptionHasCampaign = selectedSubscription?.campaign_active && selectedSubscription?.campaign_price;

  return (
    <div className="bg-gray-50 rounded-xl p-6 space-y-4">
      <h3 className="font-semibold text-gray-900">{content?.step4_title || "Sammanfattning"}</h3>
      
      {selectedPackage && (
        <div className="flex justify-between items-center py-2 border-b border-gray-200">
          <div>
            <p className="text-sm text-gray-500">{content?.summary_package_label || "Paket"}</p>
            <p className="font-medium">{selectedPackage.name}</p>
          </div>
          {packageHasCampaign ? (
            <div className="text-right">
              <div className="flex items-baseline gap-2">
                <span className="font-semibold text-orange-600">{selectedPackage.campaign_price.toLocaleString("sv-SE")} kr</span>
                <span className="text-sm text-gray-400 line-through">{selectedPackage.price.toLocaleString("sv-SE")} kr</span>
              </div>
            </div>
          ) : (
            <p className="font-semibold">{selectedPackage.price.toLocaleString("sv-SE")} kr</p>
          )}
        </div>
      )}
      
      {selectedSubscription && (
        <div className="flex justify-between items-center py-2 border-b border-gray-200">
          <div>
            <p className="text-sm text-gray-500">{content?.summary_subscription_label || "Abonnemang"}</p>
            <p className="font-medium">{selectedSubscription.name}</p>
            {subscriptionHasCampaign && selectedSubscription.campaign_duration && (
              <p className="text-xs text-orange-600 font-medium">
                {selectedSubscription.campaign_text || 
                  `i ${selectedSubscription.campaign_duration} mån, därefter ${selectedSubscription.price.toLocaleString("sv-SE")} kr/mån`}
              </p>
            )}
          </div>
          {subscriptionHasCampaign ? (
            <div className="text-right">
              <div className="flex items-baseline gap-2">
                <span className="font-semibold text-orange-600">{selectedSubscription.campaign_price.toLocaleString("sv-SE")} kr/mån</span>
                <span className="text-sm text-gray-400 line-through">{selectedSubscription.price.toLocaleString("sv-SE")} kr</span>
              </div>
            </div>
          ) : (
            <p className="font-semibold">{selectedSubscription.price.toLocaleString("sv-SE")} kr/mån</p>
          )}
        </div>
      )}
      
      {selectedAddons.length > 0 && (
        <div className="py-2 border-b border-gray-200">
          <p className="text-sm text-gray-500 mb-2">{content?.summary_addons_label || "Tillval"}</p>
          {selectedAddons.map(addon => {
            const addonHasCampaign = addon.campaign_active && addon.campaign_price;
            return (
              <div key={addon.id} className="flex justify-between items-center py-1">
                <p className="font-medium">{addon.name}</p>
                {addonHasCampaign ? (
                  <div className="flex items-baseline gap-2">
                    <span className="text-sm text-orange-600 font-medium">
                      {addon.campaign_price.toLocaleString("sv-SE")} kr{addon.price_type === "monthly" && "/mån"}
                    </span>
                    <span className="text-xs text-gray-400 line-through">
                      {addon.price.toLocaleString("sv-SE")} kr
                    </span>
                  </div>
                ) : (
                  <p className="text-sm">
                    {addon.price.toLocaleString("sv-SE")} kr
                    {addon.price_type === "monthly" && "/mån"}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
      
      {selectedAddons.length === 0 && (
        <div className="py-2 border-b border-gray-200">
          <p className="text-sm text-gray-400">{content?.no_addons_text || "Inga tillval valda"}</p>
        </div>
      )}
      
      <div className="pt-4 space-y-2">
        <div className="flex justify-between items-center">
          <p className="text-gray-600">{content?.summary_onetime_label || "Engångspris"}</p>
          <p className="text-xl font-bold text-[#255E69]">{onetimeTotal.toLocaleString("sv-SE")} kr</p>
        </div>
        <div className="flex justify-between items-center">
          <p className="text-gray-600">{content?.summary_monthly_label || "Månadspris"}</p>
          <p className="text-xl font-bold text-[#255E69]">{monthlyTotal.toLocaleString("sv-SE")} kr/mån</p>
        </div>
        {subscriptionHasCampaign && selectedSubscription.campaign_duration && (
          <p className="text-xs text-orange-600 text-right">
            * Kampanjpris gäller i {selectedSubscription.campaign_duration} månader
          </p>
        )}
      </div>
    </div>
  );
};

// Success page component
const SuccessPage = ({ content }) => (
  <div className="text-center py-12">
    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
      <CheckCircle2 className="w-10 h-10 text-green-600" />
    </div>
    <h2 className="text-2xl font-bold text-gray-900 mb-4">
      {content?.success_title || "Tack för dina val!"}
    </h2>
    <p className="text-gray-600 max-w-md mx-auto">
      {content?.success_text || "Vi har tagit emot dina val och återkommer inom kort med mer information."}
    </p>
  </div>
);

// Expired/Invalid token page - Enhanced with different error types
const ExpiredPage = ({ content, error, errorCode, canRequestNew = false }) => {
  const errorMessages = {
    expired: {
      title: content?.expired_token_title || "Länken har gått ut",
      text: content?.expired_token_text || "Den här länken är inte längre giltig.",
      icon: "clock"
    },
    used: {
      title: "Länken har redan använts",
      text: "Den här länken var en engångslänk och har redan använts.",
      icon: "check"
    },
    invalid: {
      title: "Ogiltig länk",
      text: "Den här länken är inte giltig. Kontrollera att du har rätt länk.",
      icon: "alert"
    },
    revoked: {
      title: "Länken är inaktiverad",
      text: "Den här länken har inaktiverats. En ny länk kan ha skickats till dig.",
      icon: "x"
    },
    lead_not_found: {
      title: "Uppgifter saknas",
      text: "Vi kunde inte hitta dina uppgifter.",
      icon: "user"
    },
    server_error: {
      title: "Tekniskt fel",
      text: "Ett tekniskt fel uppstod. Försök igen om en stund.",
      icon: "server"
    }
  };

  const errorInfo = errorMessages[errorCode] || errorMessages.invalid;
  
  return (
    <div className="text-center py-12">
      <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
        <AlertCircle className="w-10 h-10 text-red-600" />
      </div>
      <h2 className="text-2xl font-bold text-gray-900 mb-4">
        {errorInfo.title}
      </h2>
      <p className="text-gray-600 max-w-md mx-auto mb-2">
        {errorInfo.text}
      </p>
      {canRequestNew && (
        <p className="text-sm text-gray-500 max-w-md mx-auto">
          Kontakta oss på hej@elido.nu för att få en ny länk.
        </p>
      )}
      <a 
        href="mailto:hej@elido.nu?subject=Ny konfigurator-länk"
        className="inline-flex items-center gap-2 mt-6 px-6 py-3 bg-[#255E69] text-white rounded-lg hover:bg-[#1d4a52] transition-colors"
      >
        <Mail className="w-4 h-4" />
        Kontakta oss
      </a>
    </div>
  );
};

export default function ConfiguratorPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  
  const [leadData, setLeadData] = useState(null);
  const [content, setContent] = useState({});
  const [packages, setPackages] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [addons, setAddons] = useState([]);
  
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [selectedAddons, setSelectedAddons] = useState([]);
  
  // Callback state
  const [showCallback, setShowCallback] = useState(false);
  const [callbackPhone, setCallbackPhone] = useState("");
  const [callbackMessage, setCallbackMessage] = useState("");
  const [callbackSending, setCallbackSending] = useState(false);
  const [callbackSent, setCallbackSent] = useState(false);

  // Activity tracking helper (fire-and-forget) - uses secure token-based tracking
  const trackActivity = (activityType, metadata = {}) => {
    if (!token) return;
    fetch(`${API_URL}/api/configurator/track-activity`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        token,
        activity_type: activityType,
        metadata
      })
    }).catch(() => {}); // Ignore errors - tracking should not block UX
  };

  // Legacy analytics tracking (kept for backwards compatibility)
  const trackEvent = (eventType, data = {}) => {
    fetch(`${API_URL}/api/configurator/analytics/track`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        event_type: eventType,
        token,
        lead_id: leadData?.lead_id,
        ...data
      })
    }).catch(() => {}); // Ignore errors - analytics should not block UX
  };

  // Verify token and load pricing data
  useEffect(() => {
    const verifyAndLoad = async () => {
      if (!token) {
        setError("no_token");
        setLoading(false);
        return;
      }

      // Track link opened
      trackEvent("link_opened");

      try {
        // Verify token
        const tokenRes = await fetch(`${API_URL}/api/configurator/verify-token/${token}`);
        const tokenData = await tokenRes.json();
        
        if (!tokenData.valid) {
          setError(tokenData.error || "invalid_token");
          setContent(tokenData);
          setLoading(false);
          return;
        }
        
        setTokenValid(true);
        setLeadData(tokenData);
        trackFunnel("configurator_opened", "configurator");
        
        // Load pricing data
        const pricingRes = await fetch(`${API_URL}/api/configurator/pricing`);
        const pricingData = await pricingRes.json();
        
        if (pricingData.success) {
          setPackages(pricingData.packages || []);
          setSubscriptions(pricingData.subscriptions || []);
          setAddons(pricingData.addons || {});
          setContent(pricingData.content || {});
        }
        
      } catch (err) {
        console.error("Error loading configurator:", err);
        setError("server_error");
      } finally {
        setLoading(false);
      }
    };

    verifyAndLoad();
  }, [token]);

  // Track step views
  useEffect(() => {
    if (tokenValid && currentStep) {
      trackEvent("step_viewed", { step: currentStep });
    }
  }, [currentStep, tokenValid]);


  const handleCallback = async () => {
    if (!callbackPhone.trim() || callbackPhone.trim().length < 8) return;
    setCallbackSending(true);
    try {
      trackActivity("callback_requested", { phone: callbackPhone });
      const res = await fetch(`${API_URL}/api/configurator/callback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token,
          phone: callbackPhone.trim(),
          message: callbackMessage.trim() || null
        })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setCallbackSent(true);
      } else {
        alert(data.detail || "Något gick fel. Försök igen.");
      }
    } catch (err) {
      alert("Något gick fel. Försök igen.");
    } finally {
      setCallbackSending(false);
    }
  };


  const handleSubmit = async () => {
    if (!selectedPackage || !selectedSubscription) return;
    
    setSubmitting(true);
    try {
      // Track submission attempt (fire and forget)
      trackActivity("submitted", {
        package_id: selectedPackage.id,
        package_name: selectedPackage.name,
        subscription_id: selectedSubscription.id,
        subscription_name: selectedSubscription.name,
        addon_ids: selectedAddons.map(a => a.id)
      });
      
      const res = await fetch(`${API_URL}/api/configurator/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token,
          package_id: selectedPackage.id,
          subscription_id: selectedSubscription.id,
          addon_ids: selectedAddons.map(a => a.id)
        })
      });
      
      const data = await res.json();
      
      if (res.ok && data.success) {
        // Track successful submission
        trackEvent("submitted", {
          step: 4,
          package_id: selectedPackage.id,
          subscription_id: selectedSubscription.id,
          addon_ids: selectedAddons.map(a => a.id)
        });
        setSubmitted(true);
        trackFunnel("configurator_submitted", "configurator");
      } else {
        // Show specific error from server
        const errorMsg = data.detail || data.error || data.message || "Något gick fel. Försök igen.";
        console.error("Submit failed:", res.status, errorMsg, data);
        alert(errorMsg);
      }
    } catch (err) {
      console.error("Submit error:", err);
      alert("Något gick fel. Försök igen.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleNextStep = () => {
    // Track step completion with current selections
    const trackData = { step: currentStep };
    if (currentStep === 1 && selectedPackage) {
      trackData.package_id = selectedPackage.id;
      trackActivity("package_selected", { id: selectedPackage.id, name: selectedPackage.name });
    }
    if (currentStep === 2 && selectedSubscription) {
      trackData.subscription_id = selectedSubscription.id;
      trackActivity("subscription_selected", { id: selectedSubscription.id, name: selectedSubscription.name });
    }
    if (currentStep === 3) {
      trackData.addon_ids = selectedAddons.map(a => a.id);
    }
    trackEvent("step_completed", trackData);
    trackActivity("step_changed", { from: currentStep, to: currentStep + 1 });
    
    setCurrentStep(s => s + 1);
  };

  const toggleAddon = (addon) => {
    setSelectedAddons(prev => {
      const exists = prev.find(a => a.id === addon.id);
      if (exists) {
        trackActivity("addon_toggled", { id: addon.id, name: addon.name, action: "removed" });
        return prev.filter(a => a.id !== addon.id);
      }
      trackActivity("addon_toggled", { id: addon.id, name: addon.name, action: "added" });
      return [...prev, addon];
    });
  };

  const canProceed = () => {
    if (currentStep === 1) return selectedPackage !== null;
    if (currentStep === 2) return selectedSubscription !== null;
    return true;
  };

  // Group addons by category
  const groupedAddons = addons.reduce((groups, addon) => {
    const category = addon.category || "Övrigt";
    if (!groups[category]) groups[category] = [];
    groups[category].push(addon);
    return groups;
  }, {});

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#255E69]" />
      </div>
    );
  }

  if (error || !tokenValid) {
    // Determine if user can request a new link based on error type
    const canRequestNew = ["expired", "used"].includes(error);
    
    return (
      <>
        <Helmet title="Länken är ogiltig | Elido" />
        <div className="min-h-screen bg-gray-50 py-12 px-4">
          <div className="max-w-lg mx-auto bg-white rounded-2xl shadow-sm p-8">
            <ExpiredPage 
              content={content} 
              error={error}
              errorCode={error || "invalid"}
              canRequestNew={canRequestNew}
            />
          </div>
        </div>
      </>
    );
  }

  if (submitted) {
    return (
      <>
        <Helmet title="Tack! | Elido" />
        <div className="min-h-screen bg-gray-50 py-12 px-4">
          <div className="max-w-lg mx-auto bg-white rounded-2xl shadow-sm p-8">
            <SuccessPage content={content} />
          </div>
        </div>
      </>
    );
  }

  const steps = ["Paket", "Abonnemang", "Tillval", "Sammanfattning"];
  const pageTitle = String(content?.page_title || "Välj ditt paket") + " | Elido";

  return (
    <>
      <Helmet title={pageTitle} />
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_custom-demo-gen/artifacts/x2wfas84_Elido_logotype_blue.png" 
              alt="Elido" 
              className="h-10 mx-auto mb-6"
            />
            {leadData?.company_name && (
              <p className="text-sm text-gray-500 mb-2">Konfiguration för</p>
            )}
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
              {leadData?.company_name || (content.page_title || "Välj ditt paket")}
            </h1>
            <p className="text-gray-600 max-w-xl mx-auto">
              {content.page_intro || "Anpassa din nya hemsida efter dina behov."}
            </p>
          </div>

          {/* Demo link */}
          {leadData?.demo_url && (
            <div className="bg-[#255E69]/5 border border-[#255E69]/20 rounded-lg p-4 mb-6 text-center">
              <p className="text-sm text-gray-600 mb-2">Se din demo:</p>
              <a 
                href={leadData.demo_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-[#255E69] hover:underline font-medium"
              >
                {leadData.demo_url}
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          )}

          {/* Progress */}
          <ProgressIndicator currentStep={currentStep} steps={steps} content={content} />

          {/* Content */}
          <div className="bg-white rounded-2xl shadow-sm p-6 sm:p-8">
            {/* Step 1: Packages */}
            {currentStep === 1 && (
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  {content.step1_title || "Välj paket"}
                </h2>
                <p className="text-gray-600 mb-6">
                  {content.step1_description || "Välj det paket som passar ditt företag bäst."}
                </p>
                <div className="grid gap-4 md:grid-cols-3">
                  {packages.map(pkg => (
                    <SelectionCard
                      key={pkg.id}
                      item={pkg}
                      isSelected={selectedPackage?.id === pkg.id}
                      onSelect={setSelectedPackage}
                      type="package"
                      content={content}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Step 2: Subscriptions */}
            {currentStep === 2 && (
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  {content.step2_title || "Välj abonnemang"}
                </h2>
                <p className="text-gray-600 mb-6">
                  {content.step2_description || "Välj hur du vill att vi sköter din hemsida."}
                </p>
                <div className="grid gap-4 md:grid-cols-3">
                  {subscriptions.map(sub => (
                    <SelectionCard
                      key={sub.id}
                      item={sub}
                      isSelected={selectedSubscription?.id === sub.id}
                      onSelect={setSelectedSubscription}
                      type="subscription"
                      content={content}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Step 3: Addons */}
            {currentStep === 3 && (
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  {content.step3_title || "Tillval"}
                </h2>
                <p className="text-gray-600 mb-6">
                  {content.step3_description || "Lägg till extra funktioner efter behov."}
                </p>
                
                {Object.entries(groupedAddons).map(([category, categoryAddons]) => (
                  <div key={category} className="mb-6">
                    <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                      {category}
                    </h3>
                    <div className="grid gap-3 md:grid-cols-2">
                      {categoryAddons.map(addon => (
                        <AddonCard
                          key={addon.id}
                          item={addon}
                          isSelected={selectedAddons.some(a => a.id === addon.id)}
                          onToggle={toggleAddon}
                          content={content}
                        />
                      ))}
                    </div>
                  </div>
                ))}
                
                {addons.length === 0 && (
                  <p className="text-gray-500 text-center py-8">Inga tillval tillgängliga</p>
                )}
              </div>
            )}

            {/* Step 4: Summary */}
            {currentStep === 4 && (
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  {content.step4_title || "Sammanfattning"}
                </h2>
                <p className="text-gray-600 mb-6">
                  {content.step4_description || "Granska dina val innan du skickar."}
                </p>
                <Summary
                  selectedPackage={selectedPackage}
                  selectedSubscription={selectedSubscription}
                  selectedAddons={selectedAddons}
                  content={content}
                />
              </div>
            )}

            {/* Navigation */}
            <div className="flex justify-between items-center mt-8 pt-6 border-t">
              <button
                onClick={() => setCurrentStep(s => s - 1)}
                disabled={currentStep === 1}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  currentStep === 1
                    ? "text-gray-300 cursor-not-allowed"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                <ChevronLeft className="w-5 h-5" />
                {content.back_button_text || "Tillbaka"}
              </button>
              
              {currentStep < 4 ? (
                <button
                  onClick={handleNextStep}
                  disabled={!canProceed()}
                  className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors ${
                    canProceed()
                      ? "bg-[#255E69] text-white hover:bg-[#1d4a52]"
                      : "bg-gray-200 text-gray-400 cursor-not-allowed"
                  }`}
                >
                  {content.next_button_text || "Fortsätt"}
                  <ChevronRight className="w-5 h-5" />
                </button>
              ) : (
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setShowCallback(true)}
                    className="flex items-center gap-2 px-4 py-3 border border-[#255E69] text-[#255E69] rounded-lg font-medium hover:bg-[#255E69]/5 transition-colors"
                    data-testid="callback-btn-step4"
                  >
                    <Phone className="w-4 h-4" />
                    Ring mig istället
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={submitting}
                    className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors disabled:opacity-50"
                  >
                    {submitting ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Check className="w-5 h-5" />
                    )}
                    {content.submit_button_text || "Skicka in mina val"}
                  </button>
                </div>
              )}
            </div>
            
            {/* Subtle callback link on all steps */}
            {!showCallback && !callbackSent && (
              <div className="text-center mt-4">
                <button
                  onClick={() => setShowCallback(true)}
                  className="text-sm text-gray-400 hover:text-[#255E69] transition-colors"
                  data-testid="callback-link"
                >
                  Osäker? Lämna ditt nummer så ringer vi dig
                </button>
              </div>
            )}
            
            {/* Callback form (shown when clicked) */}
            {showCallback && !callbackSent && (
              <div className="mt-6 p-5 bg-[#255E69]/5 border border-[#255E69]/20 rounded-xl" data-testid="callback-form">
                <div className="flex items-center gap-2 mb-3">
                  <Phone className="w-5 h-5 text-[#255E69]" />
                  <h3 className="font-semibold text-gray-900">Vi ringer dig</h3>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Lämna ditt nummer så hör vi av oss inom kort. Inga förpliktelser.
                </p>
                <div className="space-y-3">
                  <input
                    type="tel"
                    value={callbackPhone}
                    onChange={(e) => setCallbackPhone(e.target.value)}
                    placeholder="070-123 45 67"
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#255E69] focus:border-transparent outline-none"
                    data-testid="callback-phone-input"
                  />
                  <textarea
                    value={callbackMessage}
                    onChange={(e) => setCallbackMessage(e.target.value)}
                    placeholder="Valfritt: Beskriv kort vad du funderar på..."
                    rows={2}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#255E69] focus:border-transparent outline-none text-sm resize-none"
                    data-testid="callback-message-input"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={handleCallback}
                      disabled={callbackSending || callbackPhone.trim().length < 8}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-[#255E69] text-white rounded-lg font-medium hover:bg-[#1d4a52] transition-colors disabled:opacity-50"
                      data-testid="callback-submit-btn"
                    >
                      {callbackSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Phone className="w-4 h-4" />}
                      Ring mig
                    </button>
                    <button
                      onClick={() => setShowCallback(false)}
                      className="px-4 py-3 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      Avbryt
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Callback sent confirmation */}
            {callbackSent && (
              <div className="mt-6 p-5 bg-green-50 border border-green-200 rounded-xl text-center" data-testid="callback-sent">
                <CheckCircle2 className="w-8 h-8 text-green-600 mx-auto mb-2" />
                <p className="font-semibold text-gray-900">Tack! Vi ringer dig snart.</p>
                <p className="text-sm text-gray-600 mt-1">Du kan fortfarande göra dina val här om du vill.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
